import { PageProps } from "@/Types";
import { usePage } from "@inertiajs/vue3";
import { ComputedRef, computed } from "vue";
import { useScriptTag } from "@vueuse/core";
import {OrderResource, OrderLineResource} from "@/Types/generated.d";

export const useGoogleUtils = () => {
    const initialPageProps: ComputedRef = computed(() => usePage<PageProps>().props);

    const shouldLoadGoogleTagManager = (): boolean => {
        return !(import.meta.env.SSR || import.meta.env.DEV || !initialPageProps.value.google_tag_manager);
    };

    const shouldLoadGoogleRecaptcha = (): boolean => {
        return !(import.meta.env.SSR || import.meta.env.DEV || !initialPageProps.value.google_recaptcha_public_key);
    };

    const shouldLoadGoogleMap = (): boolean => {
        return !(import.meta.env.SSR || !initialPageProps.value.google_map_api_key);
    };

    const loadGoogleTagManager = (): string => {
        return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${initialPageProps.value.google_tag_manager}');`;
    };

    const loadDatalayerOrderValidation = (order: OrderResource): string => {
        const actionField = {
            id: order.number, // Remplacez par le numéro de commande réel
            revenue: order.total_incl_tax, // Remplacez par le montant total de la commande
            tax: order.total_tax, // Remplacez par le montant de la taxe
            shipping: order.shipping_price_incl_tax, // Remplacez par le montant des frais de port
            currency: order.currency_iso_code, // Remplacez par la devise (par exemple, EUR, USD)
        };

        const products = order.order_lines?.map((orderLine: OrderLineResource) => ({
            name: orderLine.name,// Remplacez par le nom du produit
            id: orderLine.sku, // Remplacez par l'ID du produit
            price: orderLine.price_incl_tax, // Remplacez par le prix du produit
            quantity: orderLine.quantity // Remplacez par la quantité du produit
        })) ?? [];

        return `window.dataLayer = window.dataLayer || [];

            dataLayer.push({
                'event': 'validation_commande',
                'ecommerce': {
                    purchase: {
                        actionField: ${JSON.stringify(actionField)},
                        products: ${JSON.stringify(products)}
                    }
                }
            });`;
    };

    const loadGoogleRecaptcha = (): void => {
        if (!useGoogleUtils().shouldLoadGoogleRecaptcha()) {
            return;
        }

        useScriptTag(
            `https://www.google.com/recaptcha/api.js?render=${initialPageProps.value.google_recaptcha_public_key}`,
            () => {},
            {
                async: true,
                defer: true,
            },
        );
    };

    const loadGoogleMap = (callback: () => void): void => {
        if (!useGoogleUtils().shouldLoadGoogleMap()) {
            return;
        }

        const script: HTMLScriptElement = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${initialPageProps.value.google_map_api_key}&loading=async&libraries=places&callback=initGoogleMapAutocomplete`;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);

        window.initGoogleMapAutocomplete = callback;
    };

    const showGoogleRecaptchaBadge = (showRecaptcha: boolean): string => {
        return `.grecaptcha-badge { visibility: ${showRecaptcha ? "visible" : "hidden"} !important; }`;
    };

    return {
        shouldLoadGoogleRecaptcha,
        shouldLoadGoogleTagManager,
        shouldLoadGoogleMap,
        loadGoogleTagManager,
        loadGoogleRecaptcha,
        loadGoogleMap,
        loadDatalayerOrderValidation,
        showGoogleRecaptchaBadge,
    };
};
